import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Modal } from "react-bootstrap";
import {
  fetchPurchaseOrders,
  deletePurchaseOrder,
  clearError,
  clearSuccess,
  fetchAvailableVendors,
} from "../../redux/slices/inventorySlice";
import InfoPill from "../Common/InfoPill";
import { toast } from "react-toastify";
import { FaPencilAlt, FaRegTrashAlt, FaEye } from "react-icons/fa";

const PurchaseOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    purchaseOrders = [],
    isLoadingPurchaseOrders,
    error,
    success,
    isDeletingPurchaseOrder,
    availableVendors = [],
  } = useSelector((state) => state.inventory);

  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [purchaseOrderToDelete, setPurchaseOrderToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchPurchaseOrders());
    dispatch(fetchAvailableVendors());
  }, [dispatch]);

  // Show toast messages for success and error
  useEffect(() => {
    if (error) {
      //toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      // toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const handleCreatePurchaseOrder = () => {
    navigate("/purchase-orders/create");
  };

  const handleEditPurchaseOrder = (purchaseOrder) => {
    navigate(`/purchase-orders/edit/${purchaseOrder.id}`);
  };

  const handleViewPurchaseOrder = (purchaseOrder) => {
    navigate(`/purchase-orders/details/${purchaseOrder.id}`);
  };

  const handleDeletePurchaseOrder = (purchaseOrder) => {
    setPurchaseOrderToDelete(purchaseOrder);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (purchaseOrderToDelete) {
      dispatch(deletePurchaseOrder(purchaseOrderToDelete.id));
      setShowDeleteModal(false);
      setPurchaseOrderToDelete(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedPurchaseOrders.length === 0) {
      toast.warning("Please select purchase orders to delete");
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedPurchaseOrders.length} purchase orders?`
      )
    ) {
      selectedPurchaseOrders.forEach((purchaseOrderId) => {
        dispatch(deletePurchaseOrder(purchaseOrderId));
      });
      setSelectedPurchaseOrders([]);
    }
  };

  const handleSelectPurchaseOrder = (purchaseOrderId) => {
    setSelectedPurchaseOrders((prev) =>
      prev.includes(purchaseOrderId)
        ? prev.filter((id) => id !== purchaseOrderId)
        : [...prev, purchaseOrderId]
    );
  };

  const handleSelectAll = () => {
    const safePurchaseOrders = Array.isArray(purchaseOrders)
      ? purchaseOrders
      : [];
    if (selectedPurchaseOrders.length === safePurchaseOrders.length) {
      setSelectedPurchaseOrders([]);
    } else {
      setSelectedPurchaseOrders(safePurchaseOrders.map((po) => po.id));
    }
  };

  const getPaymentStatusBadge = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const statusConfig = {
      paid: { variant: "success", text: "Paid" },
      unpaid: { variant: "danger", text: "Unpaid" },
      partial: { variant: "warning", text: "Partial" },
    };
    const config = statusConfig[key] || {
      variant: "secondary",
      text: status || "-",
    };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    const { formatDateToDDMMYYYY } = require("../../utils/dateUtils");
    return formatDateToDDMMYYYY(dateString) || "-";
  };

  const formatDateTime = (dateString) => {
    const { formatDateTimeToDDMMYYYY12Hr } = require("../../utils/dateUtils");
    return formatDateTimeToDDMMYYYY12Hr(dateString) || "-";
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const tableHeaders = [
    // {
    //   prop: "checkbox",
    //   title: (
    //     <input
    //       type="checkbox"
    //       checked={
    //         selectedPurchaseOrders.length ===
    //           (Array.isArray(purchaseOrders) ? purchaseOrders.length : 0) &&
    //         (Array.isArray(purchaseOrders) ? purchaseOrders.length : 0) > 0
    //       }
    //       onChange={handleSelectAll}
    //     />
    //   ),
    //   cell: (row) => (
    //     <input
    //       type="checkbox"
    //       checked={selectedPurchaseOrders.includes(row.id)}
    //       onChange={() => handleSelectPurchaseOrder(row.id)}
    //     />
    //   ),
    // },
    {
      prop: "invoice_number",
      title: "Invoice",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Link
          to={`/purchase-orders/details/${row.id}`}
          className="text-decoration-none fw-bold text-primary"
          style={{ cursor: "pointer" }}
        >
          {row.invoice_number}
        </Link>
      ),
    },
    {
      prop: "product_names",
      title: "Products",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <span title={row.product_names || "No products"}>
          {row.product_names && row.product_names.trim() !== ""
            ? row.product_names
            : "-"}
        </span>
      ),
    },
    {
      prop: "purchase_date",
      title: "Purchase Date",
      isSortable: true,
      cell: (row) => {
        // Use created_at for time if purchase_date doesn't have time component
        const dateTime = row.created_at || row.purchase_date;
        return formatDateTime(dateTime);
      },
    },
    {
      prop: "total_amount",
      title: "Total Amount",
      isSortable: true,
      cell: (row) => formatCurrency(row.total_amount),
    },
    {
      prop: "payment_status",
      title: "Payment Status",
      isSortable: true,
      cell: (row) => getPaymentStatusBadge(row.payment_status),
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          {/* <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleViewPurchaseOrder(row)}
            disabled={isDeletingPurchaseOrder}
          >
            <FaEye />
          </Button> */}
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => handleEditPurchaseOrder(row)}
            disabled={isDeletingPurchaseOrder}
          >
            <FaPencilAlt />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeletePurchaseOrder(row)}
            disabled={isDeletingPurchaseOrder}
          >
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoadingPurchaseOrders) {
    return <div className="text-center mt-4">Loading purchase orders...</div>;
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Purchase Orders
        </Link>
      </div>

      <DatatableWrapper
        body={Array.isArray(purchaseOrders) ? purchaseOrders : []}
        headers={tableHeaders}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
              <Filter
                placeholder="Search purchase orders..."
                className="w-100 btn-success"
              />
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill
              left="Total Purchase Orders"
              right={purchaseOrders?.length || 0}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            {selectedPurchaseOrders.length > 0 && (
              <Button
                onClick={handleBulkDelete}
                variant="danger"
                className="btn-sm"
                disabled={isDeletingPurchaseOrder}
              >
                Delete Selected ({selectedPurchaseOrders.length})
              </Button>
            )}
            <Button
              onClick={handleCreatePurchaseOrder}
              variant="success"
              className="btn-sm btn-success"
            >
              New Purchase Order
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete purchase order{" "}
          <strong>{purchaseOrderToDelete?.invoice_number}</strong>? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            disabled={isDeletingPurchaseOrder}
          >
            {isDeletingPurchaseOrder ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PurchaseOrders;
